import { Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer"
import { DraftWatermarkForPDF } from "components/Report/DraftWatermark"
import { IssueStatus, Report } from "graphql/types"
import React from "react"
import { formatMissingValue } from "utils/reportFormatting"
import { colors, coverPageStyles, documentStyles } from "./PDFStyles"

interface CoverPageProps {
  report: Report
  project: any
}

const styles = StyleSheet.create({
  ...coverPageStyles,
  page: {
    padding: 50,
    backgroundColor: "#ffffff",
  },
  textSection: {
    marginTop: 10,
    marginVertical: 20,
  },
  text: {
    fontSize: 10,
    color: colors.text,
  },
  endOfReportMarker: {
    position: "absolute",
    bottom: 80,
    left: 0,
    right: 0,
    textAlign: "center",
    fontSize: 10,
    fontWeight: "bold",
    color: colors.primary,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
})

export const CoverPage: React.FC<CoverPageProps> = ({ report, project }) => {
  // Always show watermark when status is DRAFT, regardless of showDraftWatermark flag
  const showDraftWatermark = report.issueStatus === IssueStatus.DRAFT

  // Always display NOT CLASSIFIED
  const getClassificationText = () => "NOT CLASSIFIED"

  // Get issue number (use versionNumber when not DRAFT)
  const getIssueNumber = () => {
    if (report.issueStatus === IssueStatus.DRAFT) {
      return null
    }
    return report.versionNumber
  }

  // Extract all EUT devices from the project's EUT Groups
  const getAllEUTDevices = () => {
    const debugData = {
      function: "getAllEUTDevices",
      approach: "Extract from project.devices (EUT Groups) instead of examination.deviceSetup",
      hasProject: !!project,
      projectDevicesCount: project?.devices?.length || 0,
      devices:
        project?.devices?.map((device: any, idx: number) => ({
          index: idx,
          id: device.id,
          name: device.name,
          __typename: device.__typename,
          isEutGroup: device.__typename === "DeviceEutGroup",
          eutDevices:
            device.__typename === "DeviceEutGroup"
              ? device.devices?.map((eut: any) => ({
                  id: eut.id,
                  name: eut.name,
                  type: eut.type,
                  description: eut.description,
                })) || []
              : null,
          eutDevicesCount: device.__typename === "DeviceEutGroup" ? device.devices?.length || 0 : 0,
        })) || [],
      result: null as string | null,
    }

    if (!project || !project.devices || project.devices.length === 0) {
      debugData.result = "No project or devices found"
      return null
    }

    const allEUTs: string[] = []

    // Look for EUT Groups in the project devices
    project.devices.forEach((device: any) => {
      if (device.__typename === "DeviceEutGroup" && device.devices) {
        // Each EUT Group contains actual EUT devices
        device.devices.forEach((eutDevice: any) => {
          const deviceName = eutDevice.name || eutDevice.description
          if (deviceName && !allEUTs.includes(deviceName)) {
            allEUTs.push(deviceName)
          }
        })
      }
    })

    debugData.result = allEUTs.length > 0 ? allEUTs.join(", ") : "No EUT devices found in project"
    return allEUTs.length > 0 ? allEUTs.join(", ") : null
  }

  const eutNames = getAllEUTDevices()

  // Get assignment number (from project ID or report)
  const getAssignmentNumber = () => {
    if (report.assignmentNo) return report.assignmentNo
    if (project?.id) return `PRJ-${project.id.substring(0, 8).toUpperCase()}`
    return null
  }

  // Get document number
  const getDocumentNumber = () => {
    if (report.documentNo) return report.documentNo
    const year = new Date().getFullYear()
    const reportNum = report.id?.substring(0, 3).toUpperCase() || "XXX"
    return `TRE-${year}-${reportNum}`
  }

  const classificationText = getClassificationText()
  const customer: any = (report as any).customer
  const clientName = customer?.customerName || customer?.legalName || project?.lead?.businessAccount
  const clientAddress1 = customer?.addressLine1
  const clientCityLine = [customer?.postalCode, customer?.city].filter(Boolean).join(" ")
  const clientCountry = customer?.country
  return (
    <Page size="A4" style={[documentStyles.coverPage, styles.page]}>
      {showDraftWatermark && <DraftWatermarkForPDF show={true} />}

      {/* Classification Box */}
      <View style={styles.classificationBox}>
        <Text style={styles.classificationText}>{classificationText}</Text>
      </View>

      {/* Logo Section */}
      <View style={styles.logoSection}>
        <Image
          src="/logo.png"
          style={{ width: 150, height: 30, marginBottom: 5, objectFit: "contain" }}
        />
      </View>

      {/* Test Facilities */}
      <View style={{ marginTop: 15, marginBottom: 10 }}>
        <Text style={{ fontSize: 9, fontWeight: "bold", color: colors.darkGray, marginBottom: 8 }}>
          Test Facilities:
        </Text>
        <View
          style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}
        >
          <View style={{ flexDirection: "row", gap: 40, flex: 1 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>Tempowerkring 19</Text>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>D-21079 Hamburg</Text>
              <Text style={{ fontSize: 8, color: colors.darkGray, marginTop: 2 }}>
                Tel: +49 (0)40 709 73 76 - 0
              </Text>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>
                Fax: +49 (0)40 709 73 76 - 25
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>Donaubogen 5</Text>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>D-24539 Neumünster</Text>
              <Text style={{ fontSize: 8, color: colors.darkGray, marginTop: 2 }}>
                Tel: +49 (0)4321 965 32 - 35
              </Text>
              <Text style={{ fontSize: 8, color: colors.darkGray }}>
                Fax: +49 (0)40 709 73 76 - 25
              </Text>
            </View>
          </View>
          <View style={{ alignItems: "flex-end", marginLeft: 20 }}>
            <Image src="/dakks.png" style={{ width: 120 }} />
          </View>
        </View>
      </View>

      {/* Main Title Section */}
      <View style={styles.titleSection}>
        <Text style={[styles.mainTitle, { color: colors.primary }]}>LABORATORY TEST REPORT</Text>
      </View>

      <View style={styles.divider} />

      {/* Document Information */}
      <View style={styles.infoSection}>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Document No.:</Text>
          <Text style={styles.infoValue}>{getDocumentNumber()}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Issue:</Text>
          <Text style={styles.infoValue}>
            {report.issueStatus === IssueStatus.DRAFT ? "DRAFT" : getIssueNumber()}
          </Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Assignment No.:</Text>
          <Text style={styles.infoValue}>{getAssignmentNumber()}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Equipment Under Test:</Text>
          <Text style={styles.infoValue}>{formatMissingValue(eutNames)}</Text>
        </View>
      </View>

      <View style={styles.divider} />

      {/* Client Information */}
      <View style={{ marginBottom: 15 }}>
        <Text style={{ fontSize: 10, fontWeight: "bold", color: colors.primary, marginBottom: 5 }}>
          Client:
        </Text>
        <Text style={{ fontSize: 11, marginLeft: 20 }}>{formatMissingValue(clientName)}</Text>
        {/* Client address from customer data */}
        {clientAddress1 && (
          <Text style={{ fontSize: 9, marginLeft: 20, marginTop: 3 }}>{clientAddress1}</Text>
        )}
        {clientCityLine && (
          <Text style={{ fontSize: 9, marginLeft: 20, marginTop: 2 }}>{clientCityLine}</Text>
        )}
        {clientCountry && (
          <Text style={{ fontSize: 9, marginLeft: 20, marginTop: 2 }}>{clientCountry}</Text>
        )}
        {project?.lead?.ownerEmployeeName && (
          <Text style={{ fontSize: 9, marginLeft: 20, marginTop: 3 }}>
            Contact: {project.lead.ownerEmployeeName}
          </Text>
        )}
      </View>

      {/* Signature Section */}
      <View style={styles.signatureSection}>
        <View style={styles.signatureRow}>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 10, marginBottom: 5 }}>Release Date:</Text>
            <Text style={{ fontSize: 11 }}>
              {report.releaseDate || new Date().toISOString().split("T")[0]}
            </Text>
          </View>

          <View style={{ flex: 2, marginLeft: 50 }}>
            <Text style={{ fontSize: 10, marginBottom: 2 }}>Reviewed by:</Text>
            <View
              style={{
                borderBottomWidth: 1,
                borderBottomColor: "#000",
                marginVertical: 20,
                width: "60%",
              }}
            />
            <Text style={{ fontSize: 11 }}>
              {formatMissingValue(
                report.reviewedBy?.firstName && report.reviewedBy?.lastName
                  ? `${report.reviewedBy.firstName} ${report.reviewedBy.lastName}`
                  : null
              )}
            </Text>
            {report.reviewerTitle && (
              <Text style={{ fontSize: 10, marginTop: 3 }}>{report.reviewerTitle}</Text>
            )}
          </View>
        </View>
      </View>

      {/* Text Section */}
      <View style={styles.textSection}>
        <Text style={styles.text}>
          The change in part or duplication in extracts of this test report requires the written
          approval of the laboratory. The test report refers only to the indicated samples. Test
          reports are not valid without signature. Treo is an accredited testing laboratory by the
          Deutsche Akkreditierungsstelle GmbH (DAkkS) according to DIN EN ISO/IEC 17025. The
          accreditation is valid only for the test areas listed in the accreditation certificate.
        </Text>
      </View>

      <Text
        style={styles.endOfReportMarker}
        render={({ pageNumber, totalPages }) =>
          pageNumber === totalPages ? "END OF REPORT" : ""
        }
      />

      {/* Footer with form number */}
      <View style={{ position: "absolute", bottom: 30, left: 50, right: 50 }}>
        <View style={{ borderTopWidth: 1, borderTopColor: colors.mediumGray, paddingTop: 10 }}>
          <Text style={{ fontSize: 8, color: colors.darkGray, textAlign: "center" }}>
            {formatMissingValue(report.formNumber)} Rev {formatMissingValue(report.formRevision)} |
            Page 1
          </Text>
        </View>
      </View>
    </Page>
  )
}
